import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isPremium } from "../lib/premium";
import { Button } from "../components/ui/button";
import {
  Camera, Loader2, Crown, Sparkles, ImageOff, Check, Plus, AlertCircle,
} from "lucide-react";

function PremiumGate() {
  const navigate = useNavigate();
  return (
    <div data-testid="shelfscan-premium-gate" className="bg-gradient-to-br from-amber-950/30 via-zinc-900 to-zinc-900 border border-amber-500/20 rounded-2xl p-8 text-center max-w-2xl mx-auto">
      <Crown className="w-10 h-10 text-amber-400 mx-auto mb-4" />
      <h2 className="font-heading text-2xl font-bold text-zinc-100">Shelf-scan is a Premium feature</h2>
      <p className="text-zinc-400 mt-2">Snap one photo of your shelf and let GPT-5.2 identify every visible diecast. Catalog matches are bulk-add ready.</p>
      <Button onClick={() => navigate("/billing")} className="mt-6 bg-amber-400 hover:bg-amber-300 text-black font-semibold" data-testid="shelfscan-upgrade-btn">
        <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Premium
      </Button>
    </div>
  );
}

function ItemRow({ item, isAdded, busy, onAdd }) {
  const m = item.match;
  return (
    <div
      data-testid={`shelfscan-item-${item.index}`}
      className={`flex items-center gap-3 p-3 rounded-lg ring-1 ${
        m ? "bg-zinc-900/60 ring-emerald-500/10" : "bg-zinc-900/60 ring-amber-500/10"
      }`}
    >
      {m?.model_image ? (
        <img src={resolveImageUrl(m.model_image)} alt="" className="w-14 h-14 rounded object-contain bg-zinc-800" />
      ) : (
        <div className="w-14 h-14 rounded bg-zinc-800 flex items-center justify-center">
          <ImageOff className="w-5 h-5 text-zinc-600" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-zinc-100 text-sm font-medium truncate">
          {m?.release_name || item.release_name || "Unknown model"}
        </p>
        <p className="text-zinc-500 text-xs truncate">
          {[item.brand, item.vehicle, item.colorway].filter(Boolean).join(" · ")}
          {item.position ? ` · ${item.position}` : ""}
        </p>
        {m && (
          <p className="text-emerald-400 text-[11px] mt-0.5 flex items-center gap-1">
            <Check className="w-3 h-3" /> Matched · {Math.round((m.match_score || 0) * 100)}%
            {m.already_owned && <span className="ml-2 text-zinc-500">(already owned)</span>}
          </p>
        )}
        {!m && (
          <p className="text-amber-400 text-[11px] mt-0.5 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {item.confidence} confidence · no catalog match
          </p>
        )}
      </div>
      {m && !m.already_owned && (
        <Button
          size="sm"
          onClick={() => onAdd(item)}
          disabled={busy || isAdded}
          variant={isAdded ? "ghost" : "default"}
          className={isAdded ? "text-emerald-400" : "bg-rose-600 hover:bg-rose-500 text-white"}
          data-testid={`shelfscan-add-btn-${item.index}`}
        >
          {isAdded ? <><Check className="w-3.5 h-3.5 mr-1" /> Added</> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
        </Button>
      )}
    </div>
  );
}

export default function ShelfScanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [added, setAdded] = useState({}); // { [item.index]: true }

  if (!isPremium(user)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="shelfscan-page">
        <PremiumGate />
      </div>
    );
  }

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      setScanning(true);
      try {
        const { data } = await api.post("/ai/shelf-scan", { image_base64: dataUrl });
        setResult(data);
        setAdded({});
        if (data.total_detected === 0) {
          toast.info("No diecasts detected. Try a clearer, closer photo.");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Scan failed");
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAdd = async (item) => {
    if (!item.match?.release_id) return;
    setBusy(true);
    try {
      await api.post("/collection", { release_id: item.match.release_id });
      setAdded((prev) => ({ ...prev, [item.index]: true }));
      toast.success(`Added ${item.match.release_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const handleAddAll = async () => {
    if (!result) return;
    setBusy(true);
    try {
      let added_count = 0;
      for (const it of result.items) {
        if (!it.match?.release_id) continue;
        if (it.match.already_owned) continue;
        if (added[it.index]) continue;
        try {
          await api.post("/collection", { release_id: it.match.release_id });
          setAdded((prev) => ({ ...prev, [it.index]: true }));
          added_count += 1;
        } catch { /* keep going */ }
      }
      toast.success(`Added ${added_count} models`);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setResult(null);
    setAdded({});
  };

  const matched = result?.items?.filter((i) => i.match && !i.match.already_owned) || [];

  return (
    <div data-testid="shelfscan-page" className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6 animate-fadeIn">
      <div>
        <p className="text-amber-400 text-xs uppercase tracking-[0.3em] font-semibold flex items-center gap-2">
          <Crown className="w-3.5 h-3.5" /> Premium · Shelf-scan
        </p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100 mt-2">One photo. Every model.</h1>
        <p className="text-zinc-400 mt-1">Snap your shelf, drawer, or display case. GPT-5.2 will identify each diecast and match it against the catalog.</p>
      </div>

      {!preview && (
        <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-8 sm:p-12 text-center">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="shelfscan-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <Camera className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-zinc-100 text-lg font-medium">Take or upload a shelf photo</p>
          <p className="text-zinc-500 text-sm mt-1">Up to 12 models per scan · works best with clear lighting</p>
          <Button
            onClick={() => fileRef.current?.click()}
            className="mt-6 bg-amber-400 hover:bg-amber-300 text-black font-semibold"
            data-testid="shelfscan-pick-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" /> Pick a photo
          </Button>
        </div>
      )}

      {preview && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-3">
            <img src={preview} alt="" className="w-full rounded-lg max-h-[480px] object-contain bg-black" />
            <div className="flex justify-between items-center px-2 pt-3">
              <p className="text-zinc-500 text-xs">{scanning ? "Scanning…" : result ? `${result.total_detected} detected · ${result.matched_count} matched` : ""}</p>
              <Button size="sm" variant="ghost" onClick={reset} data-testid="shelfscan-reset-btn">New photo</Button>
            </div>
          </div>

          <div>
            {scanning && (
              <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-8 text-center" data-testid="shelfscan-scanning">
                <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-3" />
                <p className="text-zinc-200">Identifying diecasts…</p>
                <p className="text-zinc-500 text-sm mt-1">Usually takes 5–15 seconds.</p>
              </div>
            )}
            {!scanning && result && (
              <div className="space-y-2">
                {matched.length > 1 && (
                  <Button onClick={handleAddAll} disabled={busy} className="w-full mb-2 bg-rose-600 hover:bg-rose-500 text-white" data-testid="shelfscan-add-all-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add all {matched.length} matches to my collection
                  </Button>
                )}
                {(result.items || []).length === 0 && (
                  <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-6 text-center text-zinc-400">
                    Couldn't detect any diecasts. Try a closer, well-lit photo.
                  </div>
                )}
                {(result.items || []).map((it) => (
                  <ItemRow
                    key={it.index}
                    item={it}
                    isAdded={!!added[it.index]}
                    busy={busy}
                    onAdd={handleAdd}
                  />
                ))}
                <div className="text-center pt-3">
                  <Button variant="ghost" size="sm" onClick={() => navigate("/collection")} className="text-zinc-400 hover:text-zinc-100" data-testid="shelfscan-go-collection-btn">
                    Done · View Collection
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
